import React, { useState, useEffect, useRef } from 'react';
import { 
  HelpCircle,
  Eye, AlertCircle,
  Calendar, User, DollarSign,
  FileText, BookOpen, Calculator,
  Hourglass,
  CheckSquare,X
} from 'lucide-react';
import { formatearFechaCompleta} from '../../../utils/utils';
import { BotonCopiar } from '../../../components/ButtonCopy';
import { procesarMensajeSolicitud } from '../../../utils/mensajes_utils';
import { useConfiguracion } from '../../../metodos/ConfiguracionContext';
import { useNavigate } from 'react-router-dom';
import { useDetalleSolicitudModel } from '../../../modelos/Solicitudes/modeloSolicitud';
import { obtenerInfoCotizaciones } from './components/FuncionSolicitudes';
import BotonGenerarCotizacion from './components/BotonGenerarCotizacionTesis';

const CardSolicitud = ({
  solicitud,
}) => {
  //menu abierto
  const menuRef = useRef(null);
  const [menuAbierto, setMenuAbierto] = useState(null);
  const { configuracion} = useConfiguracion();    
  const navigate = useNavigate();
  const {enviarNotificacionSolciitud, actualizarSolicitud} = useDetalleSolicitudModel();

  useEffect(() => {
  const handleClickOutside = (event) => {
    if (menuRef.current && !menuRef.current.contains(event.target)) {
      setMenuAbierto(null);
    }
  };

  // Solo agregar el listener si el menú está abierto
  if (menuAbierto) {
    document.addEventListener('mousedown', handleClickOutside);
  }

  // Cleanup
  return () => {
    document.removeEventListener('mousedown', handleClickOutside);
  };
  }, [menuAbierto]);


  //Filtro por estado, Disponibles - A pasar precio - Esperando
  const obtenerEstadoDisponibilidad = (solicitud) => {
    const estadoLower = solicitud.Estado;
    
    // Veiricar cotizaciones
    const tieneCotizacionesConPrecio = solicitud.cotizaciones && 
      solicitud.cotizaciones.some(cotizacion => {
        const precio = cotizacion.cotizacion || 0;
        return precio > 0;
    });
    
    if (estadoLower == "ESPERANDO") {
      return 'esperando';
    }

    if(estadoLower === "DISPONIBLE"){

      if(tieneCotizacionesConPrecio){
        return 'a_pasar_precio';
      }

      return 'disponible';

    }
     
    // Por defecto, considerar disponible
    return 'disponible';
  };

  //Prioridad de trabajo
  const calcularPrioridad = (fechaEntrega) => {
    const ahora = new Date();
    const entrega = new Date(fechaEntrega);
    const horasRestantes = (entrega - ahora) / (1000 * 60 * 60);

    if (horasRestantes < 0) return { 
      tipo: 'vencida', 
      label: 'Vencida', 
      color: 'from-gray-500 to-gray-600' };
    if (horasRestantes <= 24) return { 
      tipo: 'urgente', 
      label: '< 24hr', 
      color: 'from-red-500 to-red-600' };
    if (horasRestantes <= 36) return { 
      tipo: 'medio', 
      label: '< dia y medio', 
      color: 'from-yellow-500 to-orange-500' };
    if (horasRestantes <= 48) return { 
      tipo: 'normal', 
      label: '< 2 dias', 
      color: 'from-green-500 to-green-600' };
    return { 
      tipo: 'normal', 
      label: `${Math.ceil(horasRestantes / 24)} días`, 
      color: 'from-green-500 to-green-600' };
  };
  //Colores para servicios, icono y label
  const obtenerInfoServicio = (servicio) => {
    const servicioLower = servicio;
    
    const mapeoServicios = {
      'PARCIAL': { 
        icon: <FileText size={20} />, 
        color: 'from-green-500 to-green-600', 
        label: 'Parcial' },
      'TALLER': { 
        icon: <FileText size={20} />, 
        color: 'from-blue-500 to-blue-600', 
        label: 'Taller' },
      'TESIS': { 
        icon: <BookOpen size={20} />, 
        color: 'from-purple-500 to-purple-600', 
        label: 'Tesis' },
      'QUIZ': { 
        icon: <Calculator size={20} />, 
        color: 'from-green-500 to-green-600', 
        label: 'Quiz' },
      'CLASES PERSONALIZADAS': { 
        icon: <Calculator size={20} />, 
        color: 'from-green-500 to-green-600', 
        label: 'Clase personalizada' },
    };

    return mapeoServicios[servicioLower] || { 
      icon: <FileText size={20} />, 
      color: 'from-gray-500 to-gray-600', 
      label: servicio 
    };
  };

  //calcular tiempo
  const formatearTiempoTranscurrido = (fechaPublicacion) => {
  const { dias, horas, minutos, totalHoras } = calcularTiempoPublicado(fechaPublicacion);
  
  if (totalHoras < 1) {
    return `${minutos}m`;
  } else if (totalHoras < 24) {
    return `${horas}h ${minutos}m`;
  } else {
    return `${dias}d ${horas}h`;
  }
  };
  const calcularTiempoPublicado = (fechaPublicacion) =>{
    const ahora = new Date();
    const publicacion = new Date(fechaPublicacion);
    const tiempoTranscurrido = ahora - publicacion;

     const dias = Math.floor(tiempoTranscurrido / (1000 * 60 * 60 * 24));
    const horas = Math.floor((tiempoTranscurrido % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutos = Math.floor((tiempoTranscurrido % (1000 * 60 * 60)) / (1000 * 60));
    
    return { dias, horas, minutos, totalHoras: tiempoTranscurrido / (1000 * 60 * 60) };
  }

  //badgets
  const obtenerBadgePrioridad = (fechaEntrega) => {
    const prioridad = calcularPrioridad(fechaEntrega);
    
    const estilos = {
      urgente: 'bg-red-100 text-red-800 border-red-200',
      medio: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      normal: 'bg-green-100 text-green-800 border-green-200',
      vencida: 'bg-gray-100 text-gray-800 border-gray-200'
    };

    const iconos = {
      urgente: '🚨',
      medio: '⏰',
      normal: '✅',
      vencida: '⏰'
    };

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${estilos[prioridad.tipo]}`}>
        {iconos[prioridad.tipo]} {prioridad.label}
      </span>
    );
  };
  const obtenerBadgeDisponibilidad = (solicitud) => {
      const estado = obtenerEstadoDisponibilidad(solicitud);
      
      if (estado === 'disponible') {
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 border border-green-200">
            <CheckSquare size={12} className="mr-1" />
            Disponible
          </span>
        );
      } else if (estado === 'a_pasar_precio') {
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-800 border border-orange-200">
            <DollarSign size={12} className="mr-1" />
            A Pasar Precio
          </span>
        );
      } else {
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 border border-blue-200">
            <Hourglass size={12} className="mr-1" />
            Esperando
          </span>
        );
      }
    };

  //Ver detalles de solicitud
  const handleVerDetalles = (solicitud) => {
    // Navegar a detalles de solicitud
    navigate(`/dashboard/detalle_solicitud/${solicitud.nombreEmpresa}/${solicitud.id}`);
  };

  const handleCambiarEstado = async(value) =>{
    //actualizar solicitud
    await actualizarSolicitud(solicitud.id,solicitud.nombreEmpresa,'Estado',value,'field');
  }

  const actualizarSolicitudLocal = async(field,value) =>{
    await actualizarSolicitud(solicitud.id,solicitud.nombreEmpresa,field,value,'field');
  }

  const infoServicio = obtenerInfoServicio(solicitud.Servicio);
  const infoCotizacion = obtenerInfoCotizaciones(solicitud.cotizaciones);
  const tiempoTranscurrido = formatearTiempoTranscurrido(solicitud.fechasistema);

  return (
    <div
      key={solicitud.id}
      className={`rounded-lg p-4 shadow-md hover:shadow-lg transition-all duration-200 ${
        solicitud.ultimoIntentoSolicitud
          ? 'bg-red-50 border-2 border-red-500 hover:border-red-600 shadow-xl shadow-red-300/50' // MÁS VISIBLE
          : 'bg-white border border-gray-100 hover:border-blue-200' // Normal
      }`}
    >

      {/* Header compacto */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className={`p-1.5 rounded-lg bg-gradient-to-br ${infoServicio.color} text-white`}>
            {React.cloneElement(infoServicio.icon, { size: 16 })}
          </div>
          <div>
            <h3 className="font-bold text-gray-900 text-sm">#{solicitud.id}</h3>
            <p className="text-xs text-gray-600">{infoServicio.label}</p>
          </div>
        </div>
        
        <div className="relative flex flex-col items-end gap-1">
          {/* Tiempo transcurrido compacto */}
          <div className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
            <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
            {tiempoTranscurrido}
          </div>
          
          {/* Menú más pequeño */}
          <div className="relative" ref={menuRef}>
          {/* Botón para abrir menú */}
          <button
            onClick={() => setMenuAbierto(menuAbierto === solicitud.id ? null : solicitud.id)}
            className="p-1 hover:bg-gray-100 rounded"
          >
            ⋮
          </button>

          {/* Menú */}
          {menuAbierto === solicitud.id && (
            <div className="absolute right-0 top-8 w-36 bg-white border border-gray-200 rounded-lg shadow-lg z-50 overflow-hidden">
              
              <button
                onClick={() => {
                  handleCambiarEstado('DISPONIBLE');
                  setMenuAbierto(null);
                }}
                className="w-full text-left px-3 py-2 text-xs hover:bg-green-50 text-green-700"
              >
                ✓ DISPONIBLE
              </button>
              
              <button
                onClick={() => {
                  handleCambiarEstado('EXPIRADO');
                  setMenuAbierto(null);
                }}
                className="w-full text-left px-3 py-2 text-xs hover:bg-red-50 text-red-700"
              >
                ✗ EXPIRADO
              </button>
              
              <button
                onClick={() => {
                  handleCambiarEstado('ESPERANDO');
                  setMenuAbierto(null);
                }}
                className="w-full text-left px-3 py-2 text-xs hover:bg-yellow-50 text-yellow-700"
              >
                ⏳ ESPERANDO
              </button>

              <button
                onClick={() => {
                  handleCambiarEstado('A PAGAR');
                  setMenuAbierto(null);
                }}
                className="w-full text-left px-3 py-2 text-xs hover:bg-yellow-50 text-yellow-700"
              >
                ✅ A PAGAR
              </button>
              
              <button
                onClick={() => {
                  handleCambiarEstado('NO PODEMOS');
                  setMenuAbierto(null);
                }}
                className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50 text-gray-700"
              >
                ❌ NO PODEMOS
              </button>

              <div className="border-t border-gray-100"></div>

              <button
                onClick={() => {
                  enviarNotificacionSolciitud(solicitud);
                  setMenuAbierto(null);
                }}
                className="w-full text-left px-3 py-2 text-xs hover:bg-blue-50 flex items-center gap-2"
              >
                <Eye size={12} className="text-blue-600" />
                Notificar
              </button>

              <div className="border-t border-gray-100"></div>
              <button
                onClick={() => {
                  actualizarSolicitudLocal('ultimoIntentoSolicitud',true);
                  setMenuAbierto(null);
                }}
                className="w-full text-left px-3 py-2 text-xs hover:bg-blue-50 flex items-center gap-2"
              >
                <Eye size={12} className="text-blue-600" />
                Ult Intento 🦉
              </button>
              
            </div>
          )}
        </div>

        </div>
      </div>

      {/* Ultimo Intneto Solcitud*/}
      {solicitud.ultimoIntentoSolicitud && (
        <div className="bg-red-100 border border-red-200 rounded-md p-2 mb-3 animate-pulse">
          <p className="text-xs text-red-700 font-bold flex items-center gap-1 justify-center">
            <span>🦉</span>
            COTIZANDO EN ÚLTIMAS INSTANCIAS...
          </p>
        </div>
      )}

      {/* Materia - más compacta */}
      <div className="mb-3">
        <h4 className="text-sm font-bold text-gray-900 leading-tight line-clamp-2">
          {solicitud.materia === solicitud.predicionesIA.materiaIA
            ? solicitud.materia
            : `${solicitud.materia} - ${solicitud.predicionesIA.materiaIA}`}
        </h4>
      </div>

      {/* Información del servicio compacta */}
      <div className="bg-gray-50 rounded-md p-2 mb-3">
        <p className="text-xs text-gray-600 flex items-center gap-1">
          <span>📚</span>
          Información del servicio (programando...)
        </p>
      </div>

      {/* Fecha de entrega */}
      <div className="mb-3">
        <div className="bg-orange-100 text-orange-700 px-2 py-1 rounded-md text-xs font-medium flex items-center gap-1">
          <Calendar size={12} />
          {formatearFechaCompleta(solicitud.fechaentrega)}
        </div>
      </div>

      {/* Cotizaciones */}
      <div className="mb-3">
        {solicitud.cotizaciones?.some(cotizacion => cotizacion.cotizacion > 0) ? (
          <div className="bg-green-100 text-green-700 px-2 py-1 rounded-md text-xs font-medium flex items-center gap-1">
            <DollarSign size={12} />
            {infoCotizacion.rango}
          </div>
        ) : (
          <div className="bg-gray-100 text-gray-500 px-2 py-1 rounded-md text-xs flex items-center gap-1">
            <AlertCircle size={12} />
            Sin cotizaciones disponibles
          </div>
        )}
      </div>
      
      {/* Badges compactos */}
      <div className="mb-3 flex items-center gap-1 flex-wrap">
        {obtenerBadgePrioridad(solicitud.fechaentrega)}
        {obtenerBadgeDisponibilidad(solicitud)}
      </div>

      {/* Información adicional compacta */}
      <div className="space-y-1 mb-3">

        {/* Información de tutores con iconos bonitos */}
        <div className="mb-3">
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs text-gray-500 font-medium">Estado tutores:</span>
            {solicitud.Estado}
            <div className="flex items-center gap-3">
              {/* Tutores que han visto */}
              <div className="flex items-center gap-1">
                <div className="p-1 bg-blue-100 rounded-full">
                  <Eye size={10} className="text-blue-600" />
                </div>
                <span className="text-xs font-medium text-blue-700">
                  {solicitud.tutorVistaSolicitud.length}
                </span>
              </div>
              
              {/* Tutores que han rechazado */}
              <div className="flex items-center gap-1">
                <div className="p-1 bg-red-100 rounded-full">
                  <X size={10} className="text-red-600" />
                </div>
                <span className="text-xs font-medium text-red-700">
                  {solicitud.cotizaciones?.filter(cotizacion => cotizacion.cotizacion <= 0).length || 0}
                </span>
              </div>
              
              {/* Tutores que han cotizado */}
              <div className="flex items-center gap-1">
                <div className="p-1 bg-green-100 rounded-full">
                  <DollarSign size={10} className="text-green-600" />
                </div>
                <span className="text-xs font-medium text-green-700">
                  {solicitud.cotizaciones?.filter(cotizacion => cotizacion.cotizacion > 0).length || 0}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Pregtuntas */}
        {(() => {
        const preguntasSinResolver = solicitud.preguntas.filter(pregunta => 
          pregunta.respuesta.length == 0
        );
        
          return preguntasSinResolver.length > 0 ? (
            <div className="mb-3">
              <div className="bg-yellow-100 text-yellow-700 px-2 py-1 rounded-md text-xs font-medium flex items-center gap-1">
                <HelpCircle size={12} />
                <span>
                  {preguntasSinResolver.length} pregunta{preguntasSinResolver.length !== 1 ? 's' : ''} sin resolver
                </span>
              </div>
            </div>
          ) : null;
        })()}

        {/* Info del cliente */}
        <div className="flex items-center justify-between text-xs">
          <span className="text-gray-500 flex items-center gap-1">
            <User size={10} />
            Cliente:
          </span>
          <span className="font-medium text-gray-700 truncate ml-2">
            {solicitud.cliente || 'Sin asignar'}
          </span>
        </div>

      </div>

      {/* Especiales para tesis*/}
      {solicitud.Servicio == "TESIS" && (
        <>
          <BotonGenerarCotizacion
            solicitud={solicitud}
          />
        </>
      )}

      {/* Botones compactos */}
      <div className="flex gap-2">
        <BotonCopiar
          funcionGenerarTexto={() => procesarMensajeSolicitud(solicitud, configuracion)}
          identificador={`solicitud-${solicitud.id}`}
          className="flex-1"
        />
        <button
          onClick={() => 
            handleVerDetalles(solicitud)
          }
          className="flex-1 bg-blue-600 text-white px-3 py-2 rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center gap-1 text-xs font-medium"
        >
          <Eye size={12} />
          Ver Detalles
        </button>
      </div>

    </div>
  )
  
}

export default CardSolicitud
